/**
 * ContactCard — Reusable Enrollments + AI Upsell card.
 *
 * Iter 162 (Josh — Danny Phase 2.1)
 *
 * Same component instanced on three surfaces:
 *   • /contact/:id  (ContactFocusView)
 *   • Contacts list slide-out (ContactDetailPanel)
 *   • LeadFocus when lead has `contact_id_for_lead` (auto-linked from
 *     Closed Won — Iter 161 Phase 1)
 *
 * Features:
 *   • Full CRUD on per-product enrollments (status, patient_count,
 *     monthly_value, notes).
 *   • AI Upsell panel: hero card + ranked alternatives via Azure
 *     GPT-5 mini, server-cached 60 minutes per content hash.
 *   • Read-only-safe: passing `readOnly` hides the Add/Edit/Delete row.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import {
  Plus, Edit3, Trash2, Sparkles, Loader2, Mail, Package, CheckCircle2, AlertTriangle, X,
  ExternalLink, ListChecks, FileText, HeartPulse, AlertCircle,
} from "lucide-react";

const STATUS_COLORS = {
  active:  { bg: "#dcfce7", color: "#166534", border: "#86efac" },
  pending: { bg: "#fef3c7", color: "#854d0e", border: "#fde68a" },
  paused:  { bg: "#e0f2fe", color: "#075985", border: "#93c5fd" },
  churned: { bg: "#fee2e2", color: "#991b1b", border: "#fecaca" },
};

const STATUS_OPTIONS = ["active", "pending", "paused", "churned"];

const todayIso = () => new Date().toISOString().slice(0, 10);

// Phase 2.1 polish — derive an Account Status pill from the enrollments
// aggregate. Priority order: At risk > Paused > Healthy > Empty.
function deriveAccountStatus(data) {
  const enrollments = data?.enrollments || [];
  if (enrollments.length === 0) {
    return { key: "empty", label: "No enrollments", bg: "#f1f5f9", color: "#64748b", border: "#cbd5e1", icon: AlertCircle };
  }
  const churnedRecent = enrollments.some((e) => e.status === "churned");
  if (churnedRecent) {
    return { key: "at_risk", label: "At risk", bg: "#fee2e2", color: "#991b1b", border: "#fecaca", icon: AlertTriangle };
  }
  const paused = enrollments.some((e) => e.status === "paused");
  if (paused) {
    return { key: "paused", label: "Paused", bg: "#fef3c7", color: "#854d0e", border: "#fde68a", icon: AlertCircle };
  }
  const anyActive = enrollments.some((e) => e.status === "active");
  if (anyActive) {
    return { key: "healthy", label: "Healthy", bg: "#dcfce7", color: "#166534", border: "#86efac", icon: HeartPulse };
  }
  return { key: "pending", label: "Pending", bg: "#e0f2fe", color: "#075985", border: "#93c5fd", icon: AlertCircle };
}

// Phase 2.1 polish — lead-snapshot strip rendered above the enrollments
// table when ContactCard is mounted with a `leadContext` (LeadFocus
// surfaces always pass it).
function LeadSnapshotStrip({ lead, onOpenLead }) {
  if (!lead || !lead.id) return null;
  const stage = (lead.stage || "—").replace(/_/g, " ");
  const score = lead.ai_score ?? lead.lead_score;
  const owner = lead.owner_name || lead.owner_email || lead.owner_id;
  const lastContact = lead.last_contact_at || lead.updated_at;
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
    catch { return "—"; }
  };
  return (
    <div
      data-testid="contact-card-lead-snapshot"
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        padding: "8px 16px", borderBottom: "0.5px solid #e2e8f0",
        background: "#f8fafc", fontSize: 11, color: "#475569",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", minWidth: 0 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontSize: 9, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Stage</span>
          <span style={{ padding: "1px 7px", borderRadius: 999, background: "#e0e7ff", color: "#3730a3", fontSize: 10, fontWeight: 600, textTransform: "capitalize" }}>{stage}</span>
        </div>
        {Number.isFinite(Number(score)) && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: 9, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>AI</span>
            <span style={{ padding: "1px 7px", borderRadius: 999, background: "#fef9c3", color: "#854d0e", fontSize: 10, fontWeight: 700 }}>{Number(score)}</span>
          </div>
        )}
        {owner && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: 9, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Owner</span>
            <span style={{ fontSize: 10, fontWeight: 500, color: "#334155" }}>{owner}</span>
          </div>
        )}
        <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontSize: 9, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Last touch</span>
          <span style={{ fontSize: 10, fontWeight: 500, color: "#334155" }}>{fmtDate(lastContact)}</span>
        </div>
      </div>
      <button
        type="button"
        onClick={onOpenLead}
        data-testid="contact-card-open-lead-link"
        style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          padding: "3px 8px", fontSize: 10, fontWeight: 600,
          background: "transparent", color: "#2563eb",
          border: "0.5px solid #bfdbfe", borderRadius: 6,
          cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
        }}
      >
        Open full lead profile <ExternalLink style={{ width: 9, height: 9 }} />
      </button>
    </div>
  );
}

export default function ContactCard({ contactId, leadContext = null, readOnly = false, defaultExpanded = true }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [contactDoc, setContactDoc] = useState(null);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ enrollments: [], catalog: {}, active_count: 0, active_total_monthly: 0, active_total_patients: 0 });
  const [editingId, setEditingId] = useState(null); // null | "new" | <enrollment_id>
  const [formState, setFormState] = useState(null);
  const [aiState, setAiState] = useState({ loading: false, data: null, error: null });
  const [showAI, setShowAI] = useState(false);

  const reload = useCallback(async () => {
    if (!contactId) return;
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/contacts/${contactId}/enrollments`, { headers: authHeaders });
      setData(d || { enrollments: [], catalog: {} });
    } catch (e) {
      console.warn("enrollments fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, [contactId, authHeaders]);

  useEffect(() => { reload(); }, [reload]);

  // Fetch the contact doc so workflow buttons can use email + name.
  useEffect(() => {
    let cancelled = false;
    if (!contactId) return undefined;
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/contacts/${contactId}`, { headers: authHeaders });
        if (!cancelled) setContactDoc(d || null);
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [contactId, authHeaders]);

  const accountStatus = useMemo(() => deriveAccountStatus(data), [data]);

  const startAdd = () => {
    setEditingId("new");
    setFormState({
      product: Object.keys(data.catalog || {})[0] || "ehr",
      status: "active",
      started_at: todayIso(),
      patient_count: 0,
      monthly_value: 0,
      notes: "",
    });
  };
  const startEdit = (e) => {
    setEditingId(e.id);
    setFormState({
      product: e.product,
      status: e.status,
      started_at: e.started_at || todayIso(),
      patient_count: e.patient_count || 0,
      monthly_value: e.monthly_value || 0,
      notes: e.notes || "",
    });
  };
  const cancelEdit = () => { setEditingId(null); setFormState(null); };

  const computeMonthlyFromCatalog = useCallback((product, patient_count) => {
    const def = (data.catalog || {})[product];
    if (def?.per_patient && patient_count > 0) {
      // 50% enrollment realism — let the rep override if they want.
      return Math.round(def.per_patient * 0.5 * Number(patient_count));
    }
    return null;
  }, [data.catalog]);

  const saveForm = async () => {
    if (!formState) return;
    const body = {
      product: formState.product,
      status: formState.status,
      started_at: formState.started_at,
      patient_count: Number(formState.patient_count || 0),
      monthly_value: Number(formState.monthly_value || 0),
      notes: (formState.notes || "").trim() || null,
    };
    try {
      if (editingId === "new") {
        await axios.post(`${API}/contacts/${contactId}/enrollments`, body, { headers: authHeaders });
        toast.success("Enrollment added");
      } else {
        await axios.put(`${API}/contacts/${contactId}/enrollments/${editingId}`, body, { headers: authHeaders });
        toast.success("Enrollment updated");
      }
      cancelEdit();
      reload();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const deleteEnrollment = async (e) => {
    if (!window.confirm(`Remove ${data.catalog?.[e.product]?.label || e.product} enrollment?`)) return;
    try {
      await axios.delete(`${API}/contacts/${contactId}/enrollments/${e.id}`, { headers: authHeaders });
      toast.success("Enrollment removed");
      reload();
    } catch (err) {
      toast.error(`Delete failed: ${err?.response?.data?.detail || err.message}`);
    }
  };

  const runAIUpsell = async () => {
    setShowAI(true);
    setAiState({ loading: true, data: null, error: null });
    try {
      const { data: r } = await axios.post(
        `${API}/contacts/${contactId}/ai-upsell`,
        { lead: leadContext || null },
        { headers: authHeaders, timeout: 30000 },
      );
      setAiState({ loading: false, data: r, error: null });
    } catch (e) {
      setAiState({ loading: false, data: null, error: e?.response?.data?.detail || e.message });
    }
  };

  // Phase 2.1 polish — workflow actions on the AI Upsell hero card.
  const contactFullName = useMemo(() => {
    const f = (contactDoc?.first_name || "").trim();
    const l = (contactDoc?.last_name || "").trim();
    return [f, l].filter(Boolean).join(" ") || (contactDoc?.email || "the contact");
  }, [contactDoc]);

  const emailUpsell = useCallback((hero) => {
    const email = (contactDoc?.email || "").trim();
    const subject = `Re: ${hero?.label || "Upgrade"} for ${contactDoc?.company || contactFullName}`;
    const body = (hero?.draft_pitch || "").trim();
    // Always copy first so the rep can paste into M365/Gmail/etc.
    try { navigator.clipboard?.writeText(body); } catch { /* ignore */ }
    if (!email) {
      toast.success("Pitch copied — open Communications to send it.");
      navigate("/communications");
      return;
    }
    // Best-effort: hand the OS its native composer with a draft.
    // Falls back gracefully if the browser blocks the protocol.
    const url = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    toast.success("Draft pitch copied + composer opened");
    window.location.href = url;
  }, [contactDoc, contactFullName, navigate]);

  const addTaskForUpsell = useCallback(async (hero) => {
    if (!hero) return;
    const title = `Pitch ${hero.label} to ${contactDoc?.company || contactFullName}`;
    const due = new Date();
    due.setDate(due.getDate() + 3);
    const payload = {
      title: title.slice(0, 180),
      description: hero.draft_pitch || hero.rationale || "",
      priority: "high",
      category: "follow_up",
      due_date: due.toISOString().slice(0, 10),
      lead_id: leadContext?.id || null,
      lead_name: leadContext?.company || contactDoc?.company || null,
    };
    try {
      await axios.post(`${API}/tasks`, payload, { headers: authHeaders });
      toast.success("Follow-up task created");
    } catch (e) {
      toast.error(`Task create failed: ${e?.response?.data?.detail || e.message}`);
    }
  }, [contactDoc, contactFullName, leadContext, authHeaders]);

  const createQuoteFromUpsell = useCallback((hero) => {
    const params = new URLSearchParams();
    if (contactId) params.set("contact_id", contactId);
    if (leadContext?.id) params.set("lead_id", leadContext.id);
    if (hero?.product) params.set("product_hint", hero.product);
    navigate(`/quotes/new?${params.toString()}`);
  }, [contactId, leadContext, navigate]);

  if (loading) {
    return (
      <div data-testid="contact-card-loading" style={{ background: "var(--surface, #fff)", border: "0.5px solid var(--border, #e2e8f0)", borderRadius: 10, padding: 16, display: "flex", alignItems: "center", gap: 8, color: "#94a3b8", fontSize: 12 }}>
        <Loader2 style={{ width: 14, height: 14, animation: "spin 1s linear infinite" }} /> Loading enrollments…
      </div>
    );
  }

  const enrollments = data.enrollments || [];
  const catalog = data.catalog || {};
  const editing = editingId !== null;

  return (
    <div data-testid="contact-card-enrollments" style={{ background: "#fff", border: "0.5px solid var(--border, #e2e8f0)", borderRadius: 12, padding: 0, overflow: "hidden", fontFamily: "inherit" }}>
      {/* Phase 2.1 polish — lead snapshot mirror (rendered only when caller passes a leadContext) */}
      <LeadSnapshotStrip
        lead={leadContext}
        onOpenLead={() => leadContext?.id && navigate(`/lead-focus/${leadContext.id}`)}
      />
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "0.5px solid #e2e8f0", background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em" }}>Enrollments</div>
            {/* Phase 2.1 polish — Account Status pill */}
            <span
              data-testid={`contact-card-account-status-${accountStatus.key}`}
              title={`Account status: ${accountStatus.label}`}
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                padding: "1px 8px", borderRadius: 999, fontSize: 9,
                fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em",
                background: accountStatus.bg, color: accountStatus.color,
                border: `0.5px solid ${accountStatus.border}`,
              }}
            >
              <accountStatus.icon style={{ width: 9, height: 9 }} />
              {accountStatus.label}
            </span>
          </div>
          <div style={{ display: "flex", gap: 12, marginTop: 4, fontSize: 12, color: "#0f172a" }}>
            <span><strong style={{ color: "#0f172a" }}>{data.active_count || 0}</strong> active</span>
            <span style={{ color: "#64748b" }}>·</span>
            <span><strong style={{ color: "#059669" }}>${(data.active_total_monthly || 0).toLocaleString()}/mo</strong></span>
            <span style={{ color: "#64748b" }}>·</span>
            <span style={{ color: "#475569" }}>{(data.active_total_patients || 0).toLocaleString()} patients</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            data-testid="contact-card-ai-upsell-btn"
            onClick={runAIUpsell}
            disabled={aiState.loading}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 11px", fontSize: 11, fontWeight: 600, background: "linear-gradient(135deg, #5B6CFF 0%, #4F46E5 100%)", color: "#fff", border: 0, borderRadius: 6, cursor: aiState.loading ? "wait" : "pointer", fontFamily: "inherit" }}
          >
            {aiState.loading ? <Loader2 style={{ width: 11, height: 11, animation: "spin 1s linear infinite" }} /> : <Sparkles style={{ width: 11, height: 11 }} />}
            AI Upsell
          </button>
          {!readOnly && !editing && (
            <button
              data-testid="contact-card-add-enrollment-btn"
              onClick={startAdd}
              style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 11px", fontSize: 11, fontWeight: 600, background: "#fff", color: "#0f172a", border: "0.5px solid #cbd5e1", borderRadius: 6, cursor: "pointer", fontFamily: "inherit" }}
            >
              <Plus style={{ width: 11, height: 11 }} /> Add enrollment
            </button>
          )}
        </div>
      </div>

      {/* Enrollments table */}
      <div style={{ padding: "8px 16px 12px" }}>
        {enrollments.length === 0 && editingId !== "new" && (
          <div data-testid="contact-card-empty" style={{ padding: 18, textAlign: "center", color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>
            No enrollments yet. Click <strong>Add enrollment</strong> to record what this account is signed up for, or <strong>AI Upsell</strong> for ranked next-best-product suggestions.
          </div>
        )}
        {(enrollments.length > 0 || editingId === "new") && (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: "0.5px solid #e2e8f0" }}>
                <th style={{ textAlign: "left", padding: "6px 4px", fontSize: 9, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>Product</th>
                <th style={{ textAlign: "left", padding: "6px 4px", fontSize: 9, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>Status</th>
                <th style={{ textAlign: "right", padding: "6px 4px", fontSize: 9, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>Patients</th>
                <th style={{ textAlign: "right", padding: "6px 4px", fontSize: 9, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>$/mo</th>
                <th style={{ textAlign: "left", padding: "6px 4px", fontSize: 9, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>Started</th>
                {!readOnly && <th style={{ width: 60 }}></th>}
              </tr>
            </thead>
            <tbody>
              {enrollments.map((e) => {
                const isEditing = editingId === e.id;
                const sc = STATUS_COLORS[e.status] || STATUS_COLORS.active;
                if (isEditing && formState) {
                  return <EnrollmentEditRow key={e.id} catalog={catalog} formState={formState} setFormState={setFormState} onSave={saveForm} onCancel={cancelEdit} computeMonthlyFromCatalog={computeMonthlyFromCatalog} />;
                }
                return (
                  <tr key={e.id} data-testid={`enrollment-row-${e.id}`} style={{ borderBottom: "0.5px solid #f1f5f9" }}>
                    <td style={{ padding: "8px 4px", fontWeight: 500, color: "#0f172a" }}>{catalog?.[e.product]?.label || e.product}</td>
                    <td style={{ padding: "8px 4px" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 999, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", background: sc.bg, color: sc.color, border: `0.5px solid ${sc.border}` }}>{e.status}</span>
                    </td>
                    <td style={{ padding: "8px 4px", textAlign: "right", color: "#475569" }}>{Number(e.patient_count || 0).toLocaleString()}</td>
                    <td style={{ padding: "8px 4px", textAlign: "right", color: "#059669", fontWeight: 600 }}>${Number(e.monthly_value || 0).toLocaleString()}</td>
                    <td style={{ padding: "8px 4px", color: "#64748b", fontSize: 11 }}>{e.started_at || "—"}</td>
                    {!readOnly && (
                      <td style={{ padding: "8px 4px", textAlign: "right" }}>
                        <button onClick={() => startEdit(e)} data-testid={`enrollment-edit-${e.id}`} title="Edit" style={{ background: "transparent", border: 0, cursor: "pointer", padding: 4, color: "#64748b" }}>
                          <Edit3 style={{ width: 12, height: 12 }} />
                        </button>
                        <button onClick={() => deleteEnrollment(e)} data-testid={`enrollment-delete-${e.id}`} title="Remove" style={{ background: "transparent", border: 0, cursor: "pointer", padding: 4, color: "#dc2626" }}>
                          <Trash2 style={{ width: 12, height: 12 }} />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {editingId === "new" && formState && (
                <EnrollmentEditRow catalog={catalog} formState={formState} setFormState={setFormState} onSave={saveForm} onCancel={cancelEdit} computeMonthlyFromCatalog={computeMonthlyFromCatalog} isNew />
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* AI Upsell panel */}
      {showAI && (
        <div data-testid="contact-card-ai-upsell-panel" style={{ borderTop: "0.5px solid #e2e8f0", padding: "12px 16px 16px", background: "linear-gradient(135deg, #f5f3ff 0%, #eef2ff 100%)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Sparkles style={{ width: 13, height: 13, color: "#6366f1" }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: "#3730a3", textTransform: "uppercase", letterSpacing: "0.06em" }}>AI Upsell Analysis</span>
              {aiState.data?.cached && <span style={{ fontSize: 9, color: "#6366f1", background: "#e0e7ff", padding: "1px 6px", borderRadius: 6 }}>cached</span>}
            </div>
            <button onClick={() => setShowAI(false)} aria-label="Close AI upsell" style={{ background: "transparent", border: 0, cursor: "pointer", color: "#64748b", padding: 2 }}>
              <X style={{ width: 12, height: 12 }} />
            </button>
          </div>
          {aiState.loading && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 14, color: "#3730a3", fontSize: 12 }}>
              <Loader2 style={{ width: 14, height: 14, animation: "spin 1s linear infinite" }} /> Azure GPT-5 mini is ranking next-best products…
            </div>
          )}
          {aiState.error && (
            <div data-testid="contact-card-ai-error" style={{ display: "flex", alignItems: "flex-start", gap: 6, padding: 10, background: "#fef3c7", borderRadius: 6, fontSize: 12, color: "#92400e" }}>
              <AlertTriangle style={{ width: 13, height: 13, marginTop: 1, flexShrink: 0 }} />
              <span>{aiState.error}</span>
            </div>
          )}
          {aiState.data?.hero && (
            <div data-testid="contact-card-ai-hero" style={{ background: "#fff", borderRadius: 10, padding: 14, marginBottom: aiState.data.alternatives?.length ? 10 : 0, border: "0.5px solid #c7d2fe" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <Package style={{ width: 14, height: 14, color: "#6366f1" }} />
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{aiState.data.hero.label}</div>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 999, background: "#dcfce7", color: "#166534" }}>
                  {Math.round((aiState.data.hero.confidence || 0) * 100)}% fit
                </span>
                <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: "#059669" }}>~${Number(aiState.data.hero.monthly_value || 0).toLocaleString()}/mo</span>
              </div>
              <div style={{ fontSize: 12, color: "#334155", lineHeight: 1.45, marginBottom: 10 }}>{aiState.data.hero.rationale}</div>
              {aiState.data.hero.draft_pitch && (
                <div style={{ background: "#f8fafc", borderRadius: 8, padding: 10, fontSize: 12, color: "#0f172a", lineHeight: 1.5, whiteSpace: "pre-wrap", border: "0.5px solid #e2e8f0", marginBottom: 8 }}>{aiState.data.hero.draft_pitch}</div>
              )}
              {/* Phase 2.1 polish — workflow buttons replace the single "Copy pitch" CTA */}
              <div data-testid="contact-card-ai-hero-actions" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                <button
                  onClick={() => emailUpsell(aiState.data.hero)}
                  data-testid="contact-card-ai-email-this"
                  title={contactDoc?.email ? `Email ${contactDoc.email}` : "Open Communications"}
                  style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10, fontWeight: 600, padding: "5px 10px", borderRadius: 6, background: "#5B6CFF", color: "#fff", border: 0, cursor: "pointer", fontFamily: "inherit" }}
                >
                  <Mail style={{ width: 10, height: 10 }} /> Email this
                </button>
                <button
                  onClick={() => addTaskForUpsell(aiState.data.hero)}
                  data-testid="contact-card-ai-add-task"
                  title="Create a follow-up task pre-filled with the pitch"
                  style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10, fontWeight: 600, padding: "5px 10px", borderRadius: 6, background: "#fff", color: "#3730a3", border: "0.5px solid #c7d2fe", cursor: "pointer", fontFamily: "inherit" }}
                >
                  <ListChecks style={{ width: 10, height: 10 }} /> Add task
                </button>
                <button
                  onClick={() => createQuoteFromUpsell(aiState.data.hero)}
                  data-testid="contact-card-ai-create-quote"
                  title="Open Quote Builder pre-filled with this contact"
                  style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10, fontWeight: 600, padding: "5px 10px", borderRadius: 6, background: "#fff", color: "#0f172a", border: "0.5px solid #cbd5e1", cursor: "pointer", fontFamily: "inherit" }}
                >
                  <FileText style={{ width: 10, height: 10 }} /> Create quote
                </button>
                <button
                  onClick={() => { navigator.clipboard?.writeText(aiState.data.hero.draft_pitch || ""); toast.success("Pitch copied to clipboard"); }}
                  data-testid="contact-card-copy-pitch"
                  title="Copy the draft pitch to the clipboard"
                  style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10, fontWeight: 600, padding: "5px 10px", borderRadius: 6, background: "transparent", color: "#475569", border: "0.5px solid #cbd5e1", cursor: "pointer", fontFamily: "inherit", marginLeft: "auto" }}
                >
                  Copy pitch
                </button>
              </div>
            </div>
          )}
          {aiState.data?.alternatives?.length > 0 && (
            <div data-testid="contact-card-ai-alternatives">
              <div style={{ fontSize: 9, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Alternatives</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <tbody>
                  {aiState.data.alternatives.map((a) => (
                    <tr key={a.product} style={{ borderBottom: "0.5px solid rgba(99,102,241,0.1)" }}>
                      <td style={{ padding: "5px 4px", fontWeight: 500, color: "#0f172a" }}>{a.label}</td>
                      <td style={{ padding: "5px 4px", color: "#475569" }}>{Math.round((a.confidence || 0) * 100)}% fit</td>
                      <td style={{ padding: "5px 4px", textAlign: "right", fontWeight: 600, color: "#059669" }}>${Number(a.monthly_value || 0).toLocaleString()}/mo</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {aiState.data && !aiState.data.hero && !aiState.data.alternatives?.length && (
            <div data-testid="contact-card-ai-no-suggestions" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: 10, background: "#fff", borderRadius: 6, fontSize: 12, color: "#475569" }}>
              <CheckCircle2 style={{ width: 13, height: 13, color: "#10b981" }} />
              {aiState.data.note || "No additional upsell recommendations at this time."}
            </div>
          )}
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Inline edit row used by both Add + Edit flows ───────────────────────
function EnrollmentEditRow({ catalog, formState, setFormState, onSave, onCancel, computeMonthlyFromCatalog, isNew = false }) {
  const setField = (k, v) => setFormState((p) => ({ ...p, [k]: v }));
  const onProductChange = (newProduct) => {
    setFormState((p) => {
      const next = { ...p, product: newProduct };
      const computed = computeMonthlyFromCatalog(newProduct, p.patient_count);
      if (computed !== null && !p.monthly_value) next.monthly_value = computed;
      return next;
    });
  };
  const onPatientsChange = (n) => {
    setFormState((p) => {
      const next = { ...p, patient_count: n };
      const computed = computeMonthlyFromCatalog(p.product, n);
      if (computed !== null) next.monthly_value = computed;
      return next;
    });
  };
  return (
    <tr data-testid={isNew ? "enrollment-edit-row-new" : "enrollment-edit-row"} style={{ background: "#eff6ff" }}>
      <td style={{ padding: "6px 4px" }}>
        <select value={formState.product} onChange={(e) => onProductChange(e.target.value)} data-testid="enrollment-product-select" style={{ width: "100%", padding: "5px 6px", border: "0.5px solid #93c5fd", borderRadius: 5, fontSize: 11, fontFamily: "inherit" }}>
          {Object.entries(catalog).map(([slug, def]) => (
            <option key={slug} value={slug}>{def.label}</option>
          ))}
        </select>
      </td>
      <td style={{ padding: "6px 4px" }}>
        <select value={formState.status} onChange={(e) => setField("status", e.target.value)} data-testid="enrollment-status-select" style={{ width: "100%", padding: "5px 6px", border: "0.5px solid #93c5fd", borderRadius: 5, fontSize: 11, fontFamily: "inherit" }}>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </td>
      <td style={{ padding: "6px 4px" }}>
        <input type="number" min={0} value={formState.patient_count} onChange={(e) => onPatientsChange(Number(e.target.value))} data-testid="enrollment-patients-input" style={{ width: "100%", padding: "5px 6px", border: "0.5px solid #93c5fd", borderRadius: 5, fontSize: 11, textAlign: "right", fontFamily: "inherit" }} />
      </td>
      <td style={{ padding: "6px 4px" }}>
        <input type="number" min={0} step={1} value={formState.monthly_value} onChange={(e) => setField("monthly_value", Number(e.target.value))} data-testid="enrollment-monthly-input" style={{ width: "100%", padding: "5px 6px", border: "0.5px solid #93c5fd", borderRadius: 5, fontSize: 11, textAlign: "right", fontFamily: "inherit" }} />
      </td>
      <td style={{ padding: "6px 4px" }}>
        <input type="date" value={formState.started_at} onChange={(e) => setField("started_at", e.target.value)} data-testid="enrollment-started-input" style={{ width: "100%", padding: "5px 6px", border: "0.5px solid #93c5fd", borderRadius: 5, fontSize: 11, fontFamily: "inherit" }} />
      </td>
      <td style={{ padding: "6px 4px", textAlign: "right", whiteSpace: "nowrap" }}>
        <button onClick={onSave} data-testid="enrollment-save-btn" style={{ background: "#059669", color: "#fff", border: 0, padding: "4px 10px", borderRadius: 5, fontSize: 10, fontWeight: 600, cursor: "pointer", marginRight: 4, fontFamily: "inherit" }}>Save</button>
        <button onClick={onCancel} data-testid="enrollment-cancel-btn" style={{ background: "transparent", color: "#64748b", border: "0.5px solid #cbd5e1", padding: "4px 8px", borderRadius: 5, fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
      </td>
    </tr>
  );
}
